import { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import FontAwesome from '@expo/vector-icons/FontAwesome';

// stores
import { useLabourStore } from '../../../stores/labour';
// utils
import { convertTo12HourTime } from '../../../utils';
// styles
import { colorPalette, fontPalette, globalStyles } from '../../../static/styles/global';
import HomeDetailsHeader from '../../../components/home/HomeDetailsHeader';
import Card from '../../../components/shared/card/Card';

interface Shift {
  time_In: string | null;
  time_Out: string | null;
  scheduleHours: string | null;
  date: string;
}

interface DaySummary {
  dateObj: moment.Moment;
  formattedDate: string;
  shifts: Shift[];
}

const getMinutesFromSchedule = (scheduleHours: string | null | undefined): number => {
  if (!scheduleHours) return 0;
  const [hours, minutes] = scheduleHours.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

const formatShiftTime = (time: string | null) => {
  if (!time) return '';
  const formatted = convertTo12HourTime(time);
  if (!formatted) return '';
  return formatted.replace(' AM', 'A').replace(' PM', 'P');
};

const calculateDayHours = (shifts: Shift[]) => {
  if (!shifts || shifts.length === 0) return null;
  let totalMinutes = 0;
  shifts.forEach((shift) => {
    totalMinutes += getMinutesFromSchedule(shift.scheduleHours);
  });
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hrs}.${mins.toString().padStart(2, '0')} hrs`;
};

const WeeklySummary = ({ navigation, route }: { navigation: any; route: any }): JSX.Element => {
  const { t } = useTranslation();
  const labourStore = useLabourStore();

  const { weekDays, totals } = useMemo(() => {
    if (!labourStore.startWeekDate) {
      return { weekDays: [], totals: { days: 0, hours: 0, minutes: 0 } };
    }

    const start = moment(labourStore.startWeekDate, 'MM/DD/YYYY');
    const days: DaySummary[] = [];
    let totalShiftDaysCount = 0;
    let totalMinutesWorked = 0;

    for (let i = 0; i < 7; i++) {
      const currentDate = start.clone().add(i, 'days');
      const formattedDate = currentDate.format('YYYY-MM-DD');

      const dayShifts = (labourStore?.scheduleData?.shiftInfo || []).filter(
        (shift: any) => moment(shift.date).format('YYYY-MM-DD') === formattedDate
      ) as Shift[];

      if (dayShifts.length > 0) {
        totalShiftDaysCount += 1;

        dayShifts.forEach((shift) => {
          totalMinutesWorked += getMinutesFromSchedule(shift.scheduleHours);
        });
      }

      days.push({
        dateObj: currentDate,
        formattedDate,
        shifts: dayShifts,
      });
    }

    return {
      weekDays: days,
      totals: {
        days: totalShiftDaysCount,
        hours: Math.floor(totalMinutesWorked / 60),
        minutes: totalMinutesWorked % 60,
      },
    };
  }, [labourStore.startWeekDate, labourStore.scheduleData]);

  if (labourStore.loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0099E5" />
      </View>
    );
  }

  const getWeekString = () => {
    if (!labourStore.startWeekDate || !labourStore.endWeekDate) return '';

    const startOfWeek = moment(labourStore.startWeekDate);
    const endOfWeek = moment(labourStore.endWeekDate);

    const startDayNumber = startOfWeek.date();
    const endDayNumber = endOfWeek.date();
    const startMonth = startOfWeek.format('MMM');
    const endMonth = endOfWeek.format('MMM');

    if (startMonth === endMonth) {
      return `${startMonth} ${startDayNumber} - ${endDayNumber}`;
    } else {
      return `${startMonth} ${startDayNumber} - ${endMonth} ${endDayNumber}`;
    }
  };

  return (
    <View style={styles.container}>
      {Platform.OS === 'web' && (
        <style type="text/css">{`
          [data-testid="weekly-summary-scroll"]::-webkit-scrollbar {
            height: 8px;
            width: 8px;
            display: block;
          }
          [data-testid="weekly-summary-scroll"]::-webkit-scrollbar-track {
            background: transparent;
          }
          [data-testid="weekly-summary-scroll"]::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 4px;
          }
          [data-testid="weekly-summary-scroll"]::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
        `}</style>
      )}
      <View style={[globalStyles.primaryBgColor, globalStyles.containerBand]}>
        <View style={[globalStyles.horizontalPadGap]}>
          <HomeDetailsHeader
            title={t('My Schedule')} />
        </View>
      </View>
      <View
        style={{
          marginTop: -44,
          padding: 16,
          flex: 1,
          justifyContent: 'space-between'
        }}
      >
        <Card>
          <Text style={{
            textAlign: "center", fontSize: 16,
            lineHeight: 21, fontFamily: fontPalette.primaryBold
          }}>{getWeekString()}</Text>
          <ScrollView
            horizontal={true}
            showsHorizontalScrollIndicator={true}
            style={styles.tableScrollView}
            contentContainerStyle={{ flexGrow: 1 }}
            testID="weekly-summary-scroll"
          >
            <View style={styles.tableContainer}>
              {/* Header Row */}
              <View style={styles.headerRow}>
                {weekDays.map((day, index) => (
                  <View key={`header-${index}`} style={styles.headerCell}>
                    <Text style={styles.headerDate}>{day.dateObj.format('M/D')}</Text>
                    <Text style={styles.headerDayName}>{day.dateObj.format('dddd').toUpperCase()}</Text>
                  </View>
                ))}
                <View style={[styles.headerCell, styles.totalsColumn]}>
                  <Text style={styles.totalsHeader}>{t('TOTALS')}</Text>
                </View>
              </View>

              <View style={styles.shiftsRow}>
                {weekDays.map((day, index) => {
                  const shift = day.shifts[0];
                  return (
                    <View key={`shift1-${index}`} style={styles.cell}>
                      {shift ? (
                        <Text style={styles.shiftText}>
                          {formatShiftTime(shift.time_In)}-{formatShiftTime(shift.time_Out)}
                        </Text>
                      ) : (
                        <Text style={styles.emptyText}></Text>
                      )}
                    </View>
                  );
                })}
                <View style={[styles.cell, styles.totalsColumn]}>
                  <Text style={styles.totalsText}>
                    {totals.days} {totals.days === 1 ? t('day') : t('days')}
                  </Text>
                </View>
              </View>
              <View style={styles.spacingRow}>
                {weekDays.map((day, index) => {
                  const shift = day.shifts[1];
                  return (
                    <View key={`shift2-${index}`} style={styles.cell}>
                      {shift ? (
                        <Text style={styles.shiftText}>
                          {formatShiftTime(shift.time_In)}-{formatShiftTime(shift.time_Out)}
                        </Text>
                      ) : null}
                    </View>
                  );
                })}
                <View style={[styles.cell, styles.totalsColumn]}></View>
              </View>
              <View style={styles.hoursRow}>
                {weekDays.map((day, index) => (
                  <View key={`hour-${index}`} style={styles.cell}>
                    {day.shifts.length > 0 ? (
                      <Text style={styles.hoursText}>{calculateDayHours(day.shifts)}</Text>
                    ) : (
                      <Text style={styles.emptyText}>--</Text>
                    )}
                  </View>
                ))}
                <View style={[styles.cell, styles.totalsColumn]}>
                  <Text style={styles.totalHoursText}>
                    {totals.hours}.{totals.minutes.toString().padStart(2, '0')} {t('hrs')}
                  </Text>
                </View>
              </View>
            </View>
          </ScrollView>
        </Card>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5'
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  screenTitle: {
    color: 'white',
    fontFamily: fontPalette.primaryBold,
    fontSize: 24,
    marginBottom: 10,
    marginLeft: 15
  },
  tableScrollView: {
    marginVertical: 10,
    backgroundColor: '#fff',
    borderRadius: 8,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4
  },
  tableContainer: {
    flexDirection: 'column',
    minWidth: '100%'
  },
  headerRow: {
    flexDirection: 'row',
    backgroundColor: '#455C71'
  },
  headerCell: {
    flex: 1,
    minWidth: 100,
    paddingVertical: 12,
    paddingHorizontal: 5,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: '#5c748c'
  },
  headerDate: {
    color: '#FFFFFF',
    fontFamily: fontPalette.primaryBold,
    fontSize: 14
  },
  headerDayName: {
    color: '#FFFFFF',
    fontSize: 12,
    marginTop: 2
  },
  totalsHeader: {
    color: '#FFFFFF',
    fontFamily: fontPalette.primaryBold,
    fontSize: 14
  },
  shiftsRow: {
    flexDirection: 'row',
    backgroundColor: '#E6F0FA'
  },
  spacingRow: {
    flexDirection: 'row',
    backgroundColor: '#D9E8F6',
    height: 30
  },
  hoursRow: {
    flexDirection: 'row',
    backgroundColor: '#E6F0FA'
  },
  cell: {
    flex: 1,
    minWidth: 100,
    paddingVertical: 10,
    paddingHorizontal: 2,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: '#CDE0F5'
  },
  totalsColumn: {
    flex: 0.8,
    minWidth: 80,
    borderRightWidth: 0
  },
  shiftText: {
    color: '#5b7897',
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 2,
    fontFamily: fontPalette.primaryRegular
  },
  hoursText: {
    color: '#9C8E1C',
    fontSize: 13,
    fontFamily: fontPalette.primaryBold,
    fontStyle: 'italic'
  },
  emptyText: {
    color: '#999'
  },
  totalsText: {
    color: '#5b7897',
    fontSize: 12,
    fontFamily: fontPalette.primaryRegular
  },
  totalHoursText: {
    color: '#5b7897',
    fontSize: 13
  },
  calendarIconContainer: {
    backgroundColor: colorPalette.violet,
    borderRadius: 32,
    width: 32,
    height: 32,
    marginLeft: 5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  }
})

export default WeeklySummary;
